import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Checkbox, FormControlLabel, MenuItem } from '@mui/material'
import React from 'react'
import { FilterSelect } from '../../../components/filter-fields'
import CustomerDrawer from '../../customers/drawer/customer-drawer'
import PolicyDrawer from '../../policies/drawer/policy-drawer'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import { formatCount } from '../common/calendar-common'
import { CALENDAR_FILTERS, useCalendarViewModel } from './calendar-view-model'
import type { CalendarDayView, CalendarEventType, CalendarEventView, CalendarFilter } from './calendar-view-model'

const EVENT_VISUAL: Record<CalendarEventType, { label: string; tone: string; Icon: typeof CakeOutlinedIcon }> = {
    birthday: { label: 'Cumpleaños', tone: 'bg-tone-pink text-tone-pink-ink', Icon: CakeOutlinedIcon },
    renewal: { label: 'Renovaciones', tone: 'bg-tone-blue text-tone-blue-ink', Icon: AutorenewOutlinedIcon },
    collection: { label: 'Cobranza', tone: 'bg-tone-teal text-tone-teal-ink', Icon: PaidOutlinedIcon },
}

const FILTER_ICON: Record<CalendarFilter, typeof CakeOutlinedIcon | null> = {
    all: null,
    birthday: CakeOutlinedIcon,
    renewal: AutorenewOutlinedIcon,
    collection: PaidOutlinedIcon,
}

const navBtnClass = 'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-k-line bg-k-surface text-k-ink hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const DayCell = ({ day, compact, onOpen }: { day: CalendarDayView; compact: boolean; onOpen: (event: CalendarEventView) => void }) => (
    <div className={day.inMonth ? 'flex h-full flex-col' : 'hidden h-full md:flex md:flex-col'}>
        <div
            className={`flex h-full min-h-[88px] flex-1 flex-col rounded-xl border ${day.today
                ? 'border-k-primary bg-k-accent-soft'
                : day.inMonth
                    ? 'border-k-line bg-k-bg'
                    : 'border-transparent bg-k-line-soft'}`}
        >
            {day.inMonth && (
                <div className="flex items-center justify-between gap-2 px-2.5 pt-2">
                    <span className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-sm font-extrabold tabular-nums ${day.today ? 'bg-k-primary !text-white' : 'text-k-ink'}`}>
                        {day.dayNumber}
                    </span>
                    <span className={`text-xs capitalize text-k-muted ${compact ? '' : 'md:hidden'}`}>{day.weekday}</span>
                </div>
            )}
            <div className="flex flex-col gap-1 p-1.5">
                {day.events.map((event) => {
                    const visual = EVENT_VISUAL[event.type]
                    const Icon = visual.Icon
                    return (
                        <button
                            key={event.id}
                            type="button"
                            title={event.text}
                            disabled={!event.openable}
                            onClick={() => onOpen(event)}
                            className={`flex w-full items-start gap-1.5 rounded-lg px-2 py-1.5 text-left text-xs font-semibold leading-snug ${visual.tone} disabled:cursor-default`}
                        >
                            <Icon style={{ fontSize: 15 }} className="mt-px shrink-0" />
                            <span className="min-w-0 break-words">{event.text}</span>
                        </button>
                    )
                })}
            </div>
        </div>
    </div>
)

const CalendarSkeleton = () => (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
        <div className="flex shrink-0 flex-wrap gap-2.5 border-b border-k-line px-4 py-3.5">
            <div className="h-10 w-10 animate-pulse rounded-xl bg-k-line" />
            <div className="h-10 w-36 animate-pulse rounded-xl bg-k-line" />
            <div className="h-10 w-24 animate-pulse rounded-xl bg-k-line" />
            <div className="h-10 w-10 animate-pulse rounded-xl bg-k-line" />
            <div className="h-10 w-28 animate-pulse rounded-full bg-k-line" />
            <div className="h-10 w-32 animate-pulse rounded-full bg-k-line" />
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-auto p-3 md:grid-cols-7">
            {Array.from({ length: 35 }, (_, index) => (
                <div key={index} className="min-h-[88px] animate-pulse rounded-xl bg-k-line" />
            ))}
        </div>
    </div>
)

const CalendarView = () => {
    const model = useCalendarViewModel()

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Calendario">
            <PolicyDrawer polizaId={model.polizaId} onClose={model.closePoliza} />
            <CustomerDrawer clienteId={model.clienteId} onClose={model.closeCliente} onEdit={model.editCliente} />

            <div className="flex min-h-0 flex-1 flex-col gap-[18px] bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                <header className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 md:items-end">
                    <div className="flex flex-col gap-1">
                        <h1 className="m-0 text-xl font-extrabold tracking-[-0.03em] text-k-ink md:text-[28px]">Calendario</h1>
                        <p className="m-0 hidden text-sm text-k-muted md:block">
                            Cumpleaños, renovaciones y cobranza
                            {model.initialLoadDone ? ` · ${model.monthLabel} · ${formatCount(model.totalEvents)} eventos` : ''}
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="Actualizar"
                        title="Actualizar"
                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold text-k-ink hover:bg-k-hover md:w-auto md:px-4"
                        onClick={model.reload}
                    >
                        <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                        <span className="sr-only md:not-sr-only">Actualizar</span>
                    </button>
                </header>

                {model.loading && !model.initialLoadDone && <CalendarSkeleton />}

                {model.error && !model.loading && (
                    <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="m-0 text-sm text-k-danger-ink">{model.error}</p>
                        </div>
                        <Button variant="contained" onClick={model.reload} className="mt-3 !bg-k-danger !text-white">
                            Reintentar
                        </Button>
                    </div>
                )}

                {model.initialLoadDone && !model.error && (
                    <section className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface ${model.loading ? 'opacity-60' : ''}`}>
                        <div className="flex shrink-0 items-start gap-2.5 border-b border-k-line px-4 py-3.5">
                            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                                <button type="button" className={navBtnClass} aria-label="Mes anterior" title="Mes anterior" disabled={!model.canPrev || model.loading} onClick={model.prevMonth}>
                                    <ChevronLeftIcon style={{ fontSize: 22 }} />
                                </button>
                                <FilterSelect label="Mes" minWidth={150} value={String(model.month)} onChange={(event) => model.changeMonth(event.target.value)}>
                                    {model.months.map((name, index) => (
                                        <MenuItem key={name} value={String(index + 1)}>{name}</MenuItem>
                                    ))}
                                </FilterSelect>
                                <FilterSelect label="Año" minWidth={110} value={String(model.year)} onChange={(event) => model.changeYear(event.target.value)}>
                                    {model.years.map((item) => (
                                        <MenuItem key={item} value={String(item)}>{item}</MenuItem>
                                    ))}
                                </FilterSelect>
                                <button type="button" className={navBtnClass} aria-label="Mes siguiente" title="Mes siguiente" disabled={!model.canNext || model.loading} onClick={model.nextMonth}>
                                    <ChevronRightIcon style={{ fontSize: 22 }} />
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center rounded-xl border border-k-line bg-k-surface px-3 text-sm font-semibold text-k-ink hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40"
                                    disabled={model.isCurrentMonth || model.loading}
                                    onClick={model.goToday}
                                >
                                    Hoy
                                </button>
                                <div role="group" aria-label="Tipo de evento" className="flex flex-wrap items-center gap-2">
                                    {CALENDAR_FILTERS.map((filter) => {
                                        const on = model.type === filter.id
                                        const Icon = FILTER_ICON[filter.id]
                                        return (
                                            <button
                                                key={filter.id}
                                                type="button"
                                                aria-pressed={on}
                                                onClick={() => model.changeType(filter.id)}
                                                className={`inline-flex min-h-10 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold ${on
                                                    ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                                    : 'border-k-line-strong bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                            >
                                                {Icon && <Icon style={{ fontSize: 16 }} />}
                                                {filter.label}
                                            </button>
                                        )
                                    })}
                                </div>
                                <FormControlLabel
                                    sx={{ mx: 0, color: 'rgb(var(--k-ink))', '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={model.compact}
                                            sx={{ color: 'rgb(var(--k-muted))', '&.Mui-checked': { color: 'rgb(var(--k-primary))' } }}
                                            onChange={(event) => model.changeCompact(event.target.checked)}
                                        />
                                    )}
                                    label="Solo días con eventos"
                                />
                            </div>
                            <button
                                type="button"
                                title="Limpiar filtros"
                                aria-label="Limpiar filtros"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                                onClick={model.clearFilters}
                            >
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 overflow-auto p-3">
                            {!model.compact && (
                                <div className="mb-2 hidden grid-cols-7 gap-2 md:grid">
                                    {model.weekdays.map((name) => (
                                        <div key={name} className="rounded-lg bg-k-line-soft px-2 py-2 text-center text-xs font-bold tracking-[0.04em] text-k-muted">
                                            {name}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {model.compact && model.days.length === 0 ? (
                                <p className="m-0 px-2 py-12 text-center text-sm text-k-muted">No hay eventos en {model.monthLabel} con los filtros aplicados.</p>
                            ) : (
                                <div className={`grid gap-2 ${model.compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-7'}`}>
                                    {model.days.map((day) => (
                                        <DayCell key={day.key} day={day} compact={model.compact} onOpen={model.openEvent} />
                                    ))}
                                </div>
                            )}
                        </div>

                        <footer className="flex shrink-0 flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-k-line px-4 py-3 text-xs font-semibold text-k-ink-2">
                            {(Object.keys(EVENT_VISUAL) as CalendarEventType[]).map((type) => {
                                const visual = EVENT_VISUAL[type]
                                const Icon = visual.Icon
                                return (
                                    <span key={type} className="inline-flex items-center gap-1.5">
                                        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${visual.tone}`}>
                                            <Icon style={{ fontSize: 15 }} />
                                        </span>
                                        {visual.label}
                                    </span>
                                )
                            })}
                        </footer>
                    </section>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default CalendarView
